/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/hooks/useAuthToken.ts
 * Release: v2.0.0 - Hook per gestire autenticazione con countdown scadenza token
 * Data: Ottobre 2025
 */

import { useState, useEffect } from 'react';
import {
  isTokenExpired,
  getTokenTimeRemaining,
  formatTimeRemaining
} from '../utils/jwtUtils';

interface AuthTokenState {
  isAuthenticated: boolean;
  isExpired: boolean;
  timeRemaining: number; // in secondi
  timeRemainingFormatted: string; // formato leggibile
  token: string | null;
}

/**
 * Hook personalizzato per gestire lo stato del token JWT
 * - Controlla autenticazione
 * - Verifica scadenza
 * - Aggiorna countdown ogni secondo
 */
export function useAuthToken(): AuthTokenState {
  const [authState, setAuthState] = useState<AuthTokenState>({
    isAuthenticated: false,
    isExpired: false,
    timeRemaining: 0,
    timeRemainingFormatted: 'Scaduto',
    token: null,
  });

  useEffect(() => {
    // Funzione per aggiornare lo stato auth
    const updateAuthState = () => {
      const token = localStorage.getItem('accessToken');

      if (!token) {
        setAuthState({
          isAuthenticated: false,
          isExpired: false,
          timeRemaining: 0,
          timeRemainingFormatted: 'Scaduto',
          token: null,
        });
        return;
      }

      const expired = isTokenExpired(token);
      const remaining = getTokenTimeRemaining(token);

      setAuthState({
        isAuthenticated: !expired,
        isExpired: expired,
        timeRemaining: remaining,
        timeRemainingFormatted: formatTimeRemaining(remaining),
        token: token,
      });

      // Se scaduto, rimuovi token e redirect a login
      if (expired) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('userId');
        const path = window.location.pathname;
        if (!path.startsWith('/login') && !path.startsWith('/mobile-operator') && !path.startsWith('/mobile-officina') && !path.startsWith('/register')) {
          window.location.href = '/login';
        }
      }
    };

    // Aggiorna subito
    updateAuthState();

    // Aggiorna ogni secondo per countdown
    const interval = setInterval(updateAuthState, 1000);

    // Listener per cambiamenti localStorage (multi-tab sync)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'accessToken') {
        updateAuthState();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    // Cleanup
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return authState;
}
